import { useMemo } from 'react';
import moment from 'moment';
import { Button, DatePicker, DatePickerInput, TextInput } from '@carbon/react';
import { useDebouncedChange } from 'components/debounced-input';
import { SERVER_DATE_FORMAT } from 'utils/dates';
import styles from './admin-log.module.scss';

const SEARCH_DEBOUNCE_MS = 300;
const ENTITY_ID = /^\d*$/;

interface LogSearchFilterProps {
  id: string;
  labelText: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}

/**
 * An entity-id search box: digits only, trimmed and pushed to the filters once
 * typing pauses. A term that is not an id is flagged and never pushed.
 * @param {LogSearchFilterProps} props - The input's id and label, and the applied search and its setter
 * @return {JSX.Element} The search box
 */
export function LogSearchFilter({
  id,
  labelText,
  placeholder,
  value,
  onChange,
}: LogSearchFilterProps) {
  const [local, change] = useDebouncedChange(
    value,
    (next: string) => {
      const term = next.trim();
      if (ENTITY_ID.test(term)) onChange(term);
    },
    SEARCH_DEBOUNCE_MS
  );
  return (
    <TextInput
      id={id}
      className={styles.searchField}
      labelText={labelText}
      placeholder={placeholder}
      inputMode="numeric"
      invalid={!ENTITY_ID.test(local.trim())}
      invalidText="Enter an entity ID (numbers only)"
      value={local}
      onChange={(e) => change(e.target.value)}
    />
  );
}

interface LogDateRangeFilterProps {
  idPrefix: string;
  after: string;
  before: string;
  onChange: (range: { after: string; before: string }) => void;
}

/**
 * A from/to date range. A range is applied once both ends are picked, or
 * cleared once both are cleared; a half-picked range waits for its other end.
 * @param {LogDateRangeFilterProps} props - The input ids' prefix, the applied range and its setter
 * @return {JSX.Element} The range picker
 */
export function LogDateRangeFilter({
  idPrefix,
  after,
  before,
  onChange,
}: LogDateRangeFilterProps) {
  // Stable across renders so the picker does not reset a half-picked range.
  const value = useMemo(
    () =>
      [after, before].filter((d) => d !== '').map((d) => moment(d).toDate()),
    [after, before]
  );

  /**
   * Applies both ends, or neither.
   * @param {Date[]} dates - The picker's selected dates
   */
  const onRangeChange = (dates: Date[]) => {
    if (dates.length === 1) return;
    const [from, to] = dates;
    onChange({
      after: from ? moment(from).format(SERVER_DATE_FORMAT) : '',
      before: to ? moment(to).format(SERVER_DATE_FORMAT) : '',
    });
  };

  return (
    <DatePicker
      datePickerType="range"
      dateFormat="Y-m-d"
      value={value}
      onChange={onRangeChange}
    >
      <DatePickerInput
        id={`${idPrefix}-after`}
        labelText="From"
        placeholder="yyyy-mm-dd"
        size="md"
      />
      <DatePickerInput
        id={`${idPrefix}-before`}
        labelText="To"
        placeholder="yyyy-mm-dd"
        size="md"
      />
    </DatePicker>
  );
}

/**
 * The filter bar's Reset, pushed to its far end.
 * @param {object} props - The handler
 * @param {() => void} props.onReset - Resets the filters
 * @return {JSX.Element} The button
 */
export function LogResetButton({ onReset }: { onReset: () => void }) {
  return (
    <div className={styles.filterActions}>
      <Button kind="ghost" size="md" onClick={onReset}>
        Reset
      </Button>
    </div>
  );
}
